import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Storage } from '@ionic/storage-angular';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import languages from '@app/constants/languages';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeBg from '@angular/common/locales/bg';
import localeEl from '@angular/common/locales/el';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeGa from '@angular/common/locales/ga';
import localeHu from '@angular/common/locales/hu';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeSk from '@angular/common/locales/sk';
import localeSl from '@angular/common/locales/sl';
import localeSv from '@angular/common/locales/sv';
import { PrimeNG } from 'primeng/config';


const LNG_KEY = 'SELECTED_LANGUAGE';

// Centralized locale mapping - single source of truth for language to locale conversion
const LOCALE_MAP: { [key: string]: string } = {
  'de': 'de-DE',
  'en': 'en-US',
  'bg': 'bg-BG',
  'el': 'el-GR',
  'es': 'es-ES',
  'fi': 'fi-FI',
  'fr': 'fr-FR',
  'ga': 'ga-IE',
  'hu': 'hu-HU',
  'it': 'it-IT',
  'nl': 'nl-NL',
  'pl': 'pl-PL',
  'pt': 'pt-PT',
  'ro': 'ro-RO',
  'sk': 'sk-SK',
  'sl': 'sl-SI',
  'sv': 'sv-SE'
};

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly initialLocale: string | null;

  constructor(
    private config: PrimeNG,
    private translate: TranslateService,
    private storage: Storage,
    private router: Router
  ) {
    const locale = new URLSearchParams(window.location.search).get('locale');
    this.initialLocale = locale;
  }

  async initializeLanguage() {
    registerLocaleData(localeDe);
    registerLocaleData(localeEn);
    registerLocaleData(localeBg);
    registerLocaleData(localeEl);
    registerLocaleData(localeEs);
    registerLocaleData(localeFi);
    registerLocaleData(localeFr);
    registerLocaleData(localeGa);
    registerLocaleData(localeHu);
    registerLocaleData(localeIt);
    registerLocaleData(localeNl);
    registerLocaleData(localePl);
    registerLocaleData(localePt);
    registerLocaleData(localeRo);
    registerLocaleData(localeSk);
    registerLocaleData(localeSl);
    registerLocaleData(localeSv);

    const defaultLanguage = 'de';
    const availableLanguages = languages;

    this.translate.addLangs(availableLanguages);
    this.translate.setFallbackLang(defaultLanguage);

    // Initialize storage
    await this.storage.create();

    // Get stored language or query param, fall back to default
    const storedLanguage = await this.storage.get(LNG_KEY);
    const queryParamLanguage = this.localeFromUrl() || this.initialLocale;
    const initialLanguage = queryParamLanguage || storedLanguage || defaultLanguage;

    // Set initial language immediately
    if (availableLanguages.includes(initialLanguage)) {
      await this.setLanguage(initialLanguage);
    } else {
      await this.setLanguage(defaultLanguage);
    }

    // Do not touch the URL here: the initial navigation has not run yet, so router.url is still
    // "/" and rewriting it would send a refreshed page to the dashboard. The first NavigationEnd
    // below adds ?locale= to whatever page was actually opened.
    // A link that omits ?locale= must not reset the language chosen above.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.keepLocaleInUrl());
  }

  private localeFromUrl(): string | null {
    const locale = this.router.parseUrl(this.router.url).queryParams['locale'];
    return typeof locale === 'string' ? locale : null;
  }

  private keepLocaleInUrl() {
    const availableLanguages = languages;
    const queryLocale = this.localeFromUrl();

    if (queryLocale && availableLanguages.includes(queryLocale)) {
      if (queryLocale !== this.getSelected()) {
        void this.setLanguage(queryLocale);
      }
      return;
    }

    const lang = availableLanguages.includes(this.getSelected()) ? this.getSelected() : 'de';
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = { ...urlTree.queryParams, locale: lang };
    void this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  getSelected() {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang();
  }

  async setLanguage(lng: string) {
    this.translate.use(lng);
    await this.storage.set(LNG_KEY, lng);
    this.translate
      .get('primeng')
      .subscribe((res) => this.config.setTranslation(res));
  }

  get datePlaceholder() {
    if(this.translate.getCurrentLang() === 'de') {
      return 'TT.MM.JJJJ';
    }else{
      return 'MM/DD/YYYY';
    }
  }

  get dateFormatLocale() {
    if(this.translate.getCurrentLang() === 'de') {
      return 'dd.MM.yyyy';
    }else{
      return 'MM/dd/yyyy';
    }
  }

  get pDateFormatLocale() {
    if(this.translate.getCurrentLang() === 'de') {
      return 'dd.mm.yy';
    }else{
      return 'mm/dd/yy';
    }
  }

  /**
   * Get the proper locale identifier for the current language
   * @param lang - Optional language code. If not provided, uses current language
   * @returns Locale identifier (e.g., 'de-DE', 'en-US')
   */
  getLocaleId(lang?: string): string {
    const currentLang = lang || this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'de';
    return LOCALE_MAP[currentLang] || 'de-DE';
  }

  /**
   * Get the locale map for all supported languages
   * @returns Object mapping language codes to locale identifiers
   */
  static getLocaleMap(): { [key: string]: string } {
    return { ...LOCALE_MAP };
  }
}
